import { ESPLoader, Transport } from 'esptool-js';
import { FlashProfile, PARTITION_TABLE_OFFSET, macFromText, minifySettings, normalizeMac } from './profiles';
import { t } from '../i18n';

export function webSerialSupported(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.serial);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class LogTerminal {
  lines: string[] = [];

  constructor(private onLine?: (line: string) => void) {}

  clean() {
    this.lines = [];
  }

  writeLine(data: string) {
    this.lines.push(data);
    this.onLine?.(data);
  }

  write(data: string) {
    this.lines.push(data);
    this.onLine?.(data);
  }

  text() {
    return this.lines.join('\n');
  }
}

export interface FlashProgress {
  percent: number;
  message: string;
}

export async function requestSerialPort(): Promise<SerialPort> {
  if (!navigator.serial) {
    throw new Error(t('usb.needsHttps'));
  }
  return navigator.serial.requestPort();
}

function uint8ToBinaryString(data: Uint8Array): string {
  const parts: string[] = [];
  const chunk = 8192;
  for (let i = 0; i < data.length; i += chunk) {
    parts.push(String.fromCharCode.apply(null, Array.from(data.subarray(i, i + chunk))));
  }
  return parts.join('');
}

async function loadBootApp0(): Promise<string> {
  const response = await fetch(`${process.env.PUBLIC_URL || ''}/boot_app0.bin`);
  if (!response.ok) {
    throw new Error(t('flash.bootMissing'));
  }
  return uint8ToBinaryString(new Uint8Array(await response.arrayBuffer()));
}

async function loadPartitionTable(file: string): Promise<string> {
  const response = await fetch(`${process.env.PUBLIC_URL || ''}/${file}`);
  if (!response.ok) {
    throw new Error(t('flash.partitionsMissing'));
  }
  return uint8ToBinaryString(new Uint8Array(await response.arrayBuffer()));
}

export async function flashFirmware(options: {
  port: SerialPort;
  profile: FlashProfile;
  getImage: (profile: FlashProfile) => Promise<Uint8Array>;
  baudRate?: number;
  onProgress?: (progress: FlashProgress) => void;
}): Promise<{ chip: string; mac: string | null; log: string; profile: FlashProfile }> {
  const terminal = new LogTerminal((line) => {
    options.onProgress?.({ percent: -1, message: line });
  });
  const transport = new Transport(options.port, true);
  const loader = new ESPLoader({
    transport,
    baudrate: options.baudRate || 921600,
    romBaudrate: 115200,
    terminal,
    debugLogging: false,
  });

  const chip = await loader.main();
  let mac = macFromText(terminal.text()) || macFromText(chip);
  try {
    mac = normalizeMac(await loader.chip.readMac(loader)) || mac;
  } catch {
    // MAC from the boot log is enough
  }

  const profile = options.profile;
  options.onProgress?.({ percent: 0, message: `Detected ${chip}` });

  options.onProgress?.({ percent: 0, message: 'Downloading firmware...' });
  const firmware = await options.getImage(profile);
  if (!firmware.length) {
    try {
      await loader.after('hard_reset');
    } catch {
      // ignore
    }
    try {
      await transport.disconnect();
    } catch {
      // ignore
    }
    throw new Error(t('flash.noImage', { label: profile.label }));
  }

  const bootApp0 = await loadBootApp0();
  const partitions = await loadPartitionTable(profile.partitionTable);
  options.onProgress?.({
    percent: 0,
    message: `Writing partition table @ 0x${PARTITION_TABLE_OFFSET.toString(16)}, OTA boot slot @ 0x${profile.otadataOffset.toString(16)}, app @ 0x${profile.appOffset.toString(16)}`,
  });

  await loader.writeFlash({
    fileArray: [
      { data: partitions, address: PARTITION_TABLE_OFFSET },
      { data: bootApp0, address: profile.otadataOffset },
      { data: uint8ToBinaryString(firmware), address: profile.appOffset },
    ],
    flashMode: 'keep',
    flashFreq: 'keep',
    flashSize: profile.flashSize,
    eraseAll: false,
    compress: true,
    reportProgress: (fileIndex, written, total) => {
      if (fileIndex === 0) {
        options.onProgress?.({ percent: 1, message: 'Writing OTA partition table (ota_0 + ota_1)...' });
        return;
      }
      if (fileIndex === 1) {
        options.onProgress?.({ percent: 2, message: 'Resetting OTA boot slot so ota_0 starts...' });
        return;
      }
      const percent = total ? Math.round((written / total) * 100) : 0;
      options.onProgress?.({ percent, message: `Writing flash ${percent}%` });
    },
  });

  await loader.after('hard_reset');
  try {
    await transport.disconnect();
  } catch {
    // port may already be closing on USB-JTAG reset
  }

  return { chip, mac, log: terminal.text(), profile };
}

async function openPort(port: SerialPort, baudRate: number): Promise<void> {
  try {
    await port.close();
  } catch {
    // already closed
  }
  await port.open({ baudRate, bufferSize: 4096 });
}

async function waitForReconnect(timeoutMs: number): Promise<SerialPort | null> {
  const serial = navigator.serial;
  if (!serial) {
    return null;
  }
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      serial.removeEventListener('connect', onConnect as EventListener);
      resolve(null);
    }, timeoutMs);
    const onConnect = (event: Event) => {
      window.clearTimeout(timer);
      resolve((event as unknown as { target: SerialPort }).target);
    };
    serial.addEventListener('connect', onConnect as EventListener, { once: true });
  });
}

export async function reopenConsolePort(port: SerialPort, onLog?: (line: string) => void): Promise<SerialPort> {
  await sleep(1500);
  try {
    await openPort(port, 115200);
    return port;
  } catch {
    onLog?.('Waiting for the USB serial port to reappear...');
    const next = await waitForReconnect(15000);
    if (!next) {
      throw new Error(t('flash.portGone'));
    }
    await openPort(next, 115200);
    return next;
  }
}

async function readUntil(
  port: SerialPort,
  match: (text: string) => boolean,
  timeoutMs: number,
  onLog?: (chunk: string) => void,
): Promise<string> {
  if (!port.readable) {
    throw new Error(t('usb.notReadable'));
  }
  const reader = port.readable.getReader();
  const decoder = new TextDecoder();
  let text = '';
  const deadline = Date.now() + timeoutMs;
  try {
    while (Date.now() < deadline) {
      const remaining = deadline - Date.now();
      const result = await Promise.race([
        reader.read(),
        sleep(remaining).then(() => ({ value: undefined, done: true as const })),
      ]);
      if (result.done && !result.value) {
        break;
      }
      if (result.value) {
        const chunk = decoder.decode(result.value, { stream: true });
        text += chunk;
        onLog?.(chunk);
        if (match(text)) {
          return text;
        }
      }
      if (result.done) {
        break;
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
  return text;
}

export async function openConsolePort(port: SerialPort, baudRate = 115200): Promise<SerialPort> {
  await openPort(port, baudRate);
  return port;
}

export async function writeFileOverSerial(
  port: SerialPort,
  filePath: string,
  content: string,
  onLog?: (chunk: string) => void,
): Promise<void> {
  const normalizedPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
  let payload = content;
  try {
    payload = minifySettings(content);
  } catch {
    payload = content.replace(/\r?\n/g, ' ');
  }

  await sleep(400);
  const bootLog = await readUntil(
    port,
    (text) => /Profile:|init\r?\n|Command system initialized/i.test(text),
    8000,
    onLog,
  );
  if (!/Profile:|init/i.test(bootLog)) {
    onLog?.('\nNo boot banner (device already running). Writing file...\n');
  }

  if (!port.writable) {
    throw new Error(t('usb.notWritable'));
  }
  const writer = port.writable.getWriter();
  try {
    await writer.write(new TextEncoder().encode('\n'));
    await writer.write(new TextEncoder().encode(`write ${normalizedPath} ${payload}\n`));
  } finally {
    writer.releaseLock();
  }

  const escaped = normalizedPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const ok = new RegExp(`Wrote ${escaped}`, 'i');
  const result = await readUntil(
    port,
    (text) => ok.test(text) || /Failed to write|Invalid path|write requires content/i.test(text),
    15000,
    onLog,
  );
  if (!ok.test(result)) {
    throw new Error(t('flash.writeNotConfirmed'));
  }
}

export async function writeSettingsOverSerial(
  port: SerialPort,
  settingsJson: string,
  onLog?: (chunk: string) => void,
): Promise<void> {
  await writeFileOverSerial(port, '/settings.json', settingsJson, onLog);
}
